import type { Material, UploadStatusResponse } from '../../services/api';

export type SavePdfCopyErrorCode = 'pending' | 'unconfirmed' | 'failed' | 'authentication';

export class SavePdfCopyError extends Error {
  constructor(readonly code: SavePdfCopyErrorCode, readonly cause?: unknown) {
    super(`PDF copy save ${code}`);
    this.name = 'SavePdfCopyError';
  }
}

export interface SavePdfCopyAttempt {
  /** Keep these exact bytes across retries so upload admission can deduplicate. */
  readonly file: File;
  sessionId: string | null;
  material: Material | null;
  inFlight: Promise<Material> | null;
}

export interface SavePdfCopyDependencies {
  getToken: () => Promise<string | null>;
  uploadFile: (file: File, token: string) => Promise<string>;
  pollUploadStatus: (sessionId: string, getToken: () => Promise<string | null>) => Promise<Material | null>;
  getUploadStatus: (sessionId: string, token: string) => Promise<UploadStatusResponse>;
  refreshAndFindMaterial: (materialId: string, token: string) => Promise<Material | null>;
}

export function createSavePdfCopyAttempt(file: File): SavePdfCopyAttempt {
  return { file, sessionId: null, material: null, inFlight: null };
}

async function performSave(attempt: SavePdfCopyAttempt, deps: SavePdfCopyDependencies): Promise<Material> {
  // The caller supplies an owner-guarded token provider. Never reuse the token
  // from the initial upload after its long-running processing has finished.
  const token = await deps.getToken();
  if (!token) throw new SavePdfCopyError('authentication');

  if (attempt.sessionId) {
    // The shared library poller clears its own session on completion or error.
    // A retry must recover this acknowledged session, never enqueue another PDF.
    try {
      const response = await deps.getUploadStatus(attempt.sessionId, token);
      if (!response || response.success !== true || response.sessionId !== attempt.sessionId) {
        throw new SavePdfCopyError('unconfirmed');
      }
      if (response.uploadStatus === 'queued' || response.uploadStatus === 'processing') {
        throw new SavePdfCopyError('pending');
      }
      if (response.uploadStatus === 'failed' || response.uploadStatus === 'cancelled') {
        throw new SavePdfCopyError('failed');
      }
      if (response.uploadStatus !== 'completed'
        || typeof response.materialId !== 'string' || !response.materialId.trim()) {
        throw new SavePdfCopyError('unconfirmed');
      }
      const material = await deps.refreshAndFindMaterial(response.materialId, token);
      if (!material || material.id !== response.materialId) {
        throw new SavePdfCopyError('unconfirmed');
      }
      attempt.material = material;
      return material;
    } catch (error) {
      if (error instanceof SavePdfCopyError) throw error;
      throw new SavePdfCopyError('unconfirmed', error);
    }
  }

  // Preserve upload errors (including quota errors) for the existing UI. If its
  // response was lost, retrying this exact File uses the server's byte hash.
  const sessionId = await deps.uploadFile(attempt.file, token);
  if (typeof sessionId !== 'string' || !sessionId.trim()) {
    throw new SavePdfCopyError('unconfirmed');
  }
  attempt.sessionId = sessionId;

  try {
    const material = await deps.pollUploadStatus(sessionId, deps.getToken);
    if (!material || typeof material.id !== 'string' || !material.id.trim()) {
      throw new SavePdfCopyError('unconfirmed');
    }
    attempt.material = material;
    return material;
  } catch (error) {
    if (error instanceof SavePdfCopyError) throw error;
    throw new SavePdfCopyError('unconfirmed', error);
  }
}

/** Reuse the same attempt for retry; a new attempt means an explicit new copy. */
export function savePdfCopy(attempt: SavePdfCopyAttempt, deps: SavePdfCopyDependencies): Promise<Material> {
  if (attempt.inFlight) return attempt.inFlight;
  if (attempt.material) return Promise.resolve(attempt.material);
  const operation = performSave(attempt, deps).finally(() => {
    if (attempt.inFlight === operation) attempt.inFlight = null;
  });
  attempt.inFlight = operation;
  return operation;
}
